import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import BrandDashboardLayout from "@/components/BrandDashboardLayout";
import { Button } from "@/components/ui/button";
import { VerifiedBadge, PremiumBadge } from "@/components/Badges";
import {
  Briefcase, Inbox, Eye, Plus, Search, ArrowRight, ArrowUpRight,
  Image as ImageIcon, Check, X as XIcon, Sparkles, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { labelTalentType } from "@/lib/roleCategories";
import AdminControlledWidgets from "@/components/AdminControlledWidgets";
import DashboardHero from "@/components/DashboardHero";
import ProfileQRCard from "@/components/ProfileQRCard";
import ViewsChart from "@/components/ViewsChart";

const STATUS = {
  sent:        { label: "Nouvelle",    color: "bg-[#EEF3FF] text-[#3B5BDB] border-[#BFDBFE]", dot: "bg-[#3B5BDB]" },
  viewed:      { label: "Vue",         color: "bg-[#FAF3EA] text-[#666]   border-[#E5E5E5]", dot: "bg-[#8B8680]" },
  shortlisted: { label: "Shortlistée", color: "bg-[#FAF3EA] text-[#8B5A2B] border-[#E6C29A]", dot: "bg-[#C68E59]" },
  accepted:    { label: "Acceptée",    color: "bg-[#E7F5EC] text-[#2E7D32] border-[#BFE4C5]", dot: "bg-[#2E7D32]" },
  refused:     { label: "Refusée",     color: "bg-red-50 text-[#D32F2F] border-red-200",     dot: "bg-[#D32F2F]" },
};

export default function ProDashboard() {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [talents, setTalents] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user && !user.role) navigate("/onboarding");
    if (!loading && user && (user.role !== "creator" && user.role !== "professional")) {
      if (user.role === "talent") navigate("/talent");
      else if (user.role === "admin") navigate("/admin");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if ((user?.role === "creator" || user?.role === "professional")) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadAll = async () => {
    const [s, a, t] = await Promise.all([
      api.get("/pro/stats").catch(() => ({ data: null })),
      api.get("/pro/applications").catch(() => ({ data: [] })),
      api.get("/talents/discover", { params: { limit: 4 } }).catch(() => ({ data: [] })),
    ]);
    setStats(s.data || { active_collabs: 0, apps_total: 0, apps_new: 0 });
    setApps(Array.isArray(a.data) ? a.data : []);
    setTalents(Array.isArray(t.data) ? t.data : []);
  };

  const updateAppStatus = async (appId, newStatus) => {
    try {
      await api.put(`/applications/${appId}`, { status: newStatus });
      setApps((prev) => prev.map((a) => a.id === appId ? { ...a, status: newStatus } : a));
      toast.success(newStatus === "accepted" ? "Candidature acceptée" : newStatus === "refused" ? "Candidature refusée" : "Mise à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (!user || !profile || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF5]" data-testid="pro-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-serif text-lg text-[#1A1A1A]">Chargement…</p>
        </div>
      </div>
    );
  }

  const brandName = profile?.company_name || user?.name || "Marque";
  const recentApps = apps.slice(0, 4);
  const profileViews = stats.views_total ?? profile?.profile_views_total ?? 0;

  return (
    <BrandDashboardLayout>
      <DashboardHero />
      <AdminControlledWidgets />
      {/* ============ HEADER ============ */}
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4" data-testid="pro-dash-header">
        <div>
          <p className="caption-label text-[#C68E59]">Tableau de bord</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1 leading-[1.05]">
            Bonjour, <span className="italic">{brandName}</span>
          </h1>
          <p className="text-[#666] mt-2 text-sm">L'essentiel pour faire avancer vos projets.</p>
        </div>
        <div className="flex items-center gap-2">
          {profile?.verified && <VerifiedBadge />}
          {profile?.is_premium && <PremiumBadge />}
        </div>
      </div>

      {/* ============ HERO ACTION ============ */}
      <section
        className="relative mb-10 rounded-3xl overflow-hidden bg-[#1A1A1A] text-white shadow-[0_20px_60px_-30px_rgba(0,0,0,0.5)]"
        data-testid="hero-action"
      >
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-[#C68E59]/30 blur-[140px]" />
        </div>
        <div className="relative p-8 sm:p-10 lg:p-14 grid sm:grid-cols-3 gap-8 items-center">
          <div className="sm:col-span-2">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#E6C29A]">
              <Sparkles className="w-3.5 h-3.5" /> Action principale
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">
              Publier une <span className="italic text-[#E6C29A]">collaboration</span>
            </h2>
            <p className="text-white/80 text-sm sm:text-base mt-4 max-w-xl leading-relaxed">
              Décrivez votre projet en quelques minutes — recevez des candidatures de talents Helencia dans la journée.
            </p>
            <Link to="/createurs/collaborations?new=1" className="inline-flex mt-7" data-testid="hero-cta">
              <Button className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-8 h-12 text-sm font-semibold tracking-wide shadow-[0_15px_40px_-15px_rgba(198,142,89,0.7)]">
                <Plus className="w-4 h-4 mr-2" /> Publier une collaboration <ArrowUpRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
          <div className="hidden sm:flex items-center justify-end">
            <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-white/8 border border-white/15 flex items-center justify-center backdrop-blur-sm">
              <Briefcase className="w-12 h-12 lg:w-16 lg:h-16 text-[#E6C29A]" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ 4 KPIs ============ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="kpi-row">
        <KPI
          icon={Inbox}
          label="Candidatures reçues"
          value={stats.apps_total}
          sub={stats.apps_new > 0 ? `${stats.apps_new} nouvelle${stats.apps_new > 1 ? "s" : ""} à traiter` : "Aucune nouvelle candidature"}
          highlight={stats.apps_new > 0}
          to="/createurs/applications"
          testid="kpi-apps"
        />
        <KPI
          icon={Briefcase}
          label="Collabs actives"
          value={stats.active_collabs}
          sub={`${stats.total_collabs ?? stats.active_collabs} publiée${(stats.total_collabs ?? 0) > 1 ? "s" : ""} au total`}
          to="/createurs/collaborations"
          testid="kpi-collabs"
        />
        <KPI
          icon={Check}
          label="Talents recrutés"
          value={stats.apps_accepted ?? 0}
          sub={stats.apps_shortlisted > 0 ? `${stats.apps_shortlisted} shortlisté${stats.apps_shortlisted > 1 ? "s" : ""}` : "Candidatures acceptées"}
          to="/createurs/applications"
          testid="kpi-accepted"
        />
        <KPI
          icon={Eye}
          label="Vues du profil"
          value={profileViews}
          sub={`${stats.views_week ?? 0} cette semaine · ${(stats.views_trend ?? 0) >= 0 ? "+" : ""}${stats.views_trend ?? 0}%`}
          to={`/createurs/${user.user_id}`}
          testid="kpi-views"
        />
      </div>

      {/* ============ ROW: Views chart + QR ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10" data-testid="stats-row">
        <div className="lg:col-span-5">
          <ViewsChart chart={stats.chart || []} total={stats.views_total ?? 0} week={stats.views_week ?? 0} trend={stats.views_trend ?? 0}
            subtitle={stats.favorites > 0 ? `${stats.favorites} talent${stats.favorites > 1 ? "s" : ""} en favoris` : undefined} />
        </div>
        <div className="lg:col-span-7">
          <ProfileQRCard userId={user.user_id} role="creator" name={brandName} />
        </div>
      </div>

      {/* ============ QUICK ACTIONS ============ */}
      <div className="mb-4">
        <p className="caption-label text-[#A26F3E]">Actions rapides</p>
        <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1">Gagnez du temps</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12" data-testid="quick-actions">
        <QuickAction icon={Plus}    label="Publier une collaboration" to="/createurs/collaborations?new=1" testid="qa-publish" />
        <QuickAction icon={Search}  label="Rechercher un talent"      to="/createurs/talents"            testid="qa-search" />
        <QuickAction icon={Inbox}   label="Voir les candidatures"     to="/createurs/applications"       testid="qa-apps" badge={stats.apps_new} />
      </div>

      {/* ============ ROW: Recent applications + Talent suggestions ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">

        {/* Recent applications */}
        <div className="lg:col-span-7 bg-white rounded-3xl border border-[#EADDC9] p-6 sm:p-7" data-testid="recent-apps-card">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <p className="caption-label text-[#A26F3E]">Dernières candidatures</p>
              <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1">À traiter</h3>
            </div>
            <Link to="/createurs/applications" className="text-xs text-[#1A1A1A] hover:underline inline-flex items-center gap-1">
              Tout voir <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {recentApps.length === 0 ? (
            <div className="rounded-2xl bg-[#FAF3EA] p-8 text-center">
              <Inbox className="w-8 h-8 text-[#B3A78C] mx-auto mb-3" />
              <p className="text-sm text-[#8B8680]">Aucune candidature pour l'instant.</p>
            </div>
          ) : (
            <ul className="divide-y divide-[#FAF3EA]">
              {recentApps.map((a) => (
                <ApplicationRow key={a.id} app={a} onUpdate={updateAppStatus} />
              ))}
            </ul>
          )}
        </div>

        {/* Talent suggestions */}
        <div className="lg:col-span-5 bg-white rounded-3xl border border-[#EADDC9] p-6 sm:p-7" data-testid="talent-suggestions">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <p className="caption-label text-[#C68E59]">Suggestions</p>
              <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1">Talents à découvrir</h3>
            </div>
            <Link to="/createurs/talents" className="text-xs text-[#1A1A1A] hover:underline inline-flex items-center gap-1">
              Tous <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {talents.length === 0 ? (
            <div className="rounded-2xl bg-[#FAF3EA] p-6 text-center">
              <ImageIcon className="w-7 h-7 text-[#B3A78C] mx-auto mb-2" />
              <p className="text-sm text-[#8B8680]">Aucun talent disponible.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {talents.slice(0, 4).map((t) => (
                <TalentSuggestion key={t.user_id} talent={t} />
              ))}
            </div>
          )}
        </div>
      </div>
    </BrandDashboardLayout>
  );
}

/* ============================== SUBCOMPONENTS ============================== */

function KPI({ icon: Icon, label, value, sub, highlight, testid, to }) {
  const cls = `relative rounded-3xl p-6 border transition ${
    highlight
      ? "bg-gradient-to-br from-[#FAF3EA] to-[#F5E6D0] border-[#E6C29A]"
      : "bg-white border-[#EADDC9]"
  } ${to ? "hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] hover:border-[#C68E59]/40" : ""}`;
  const inner = (
    <>
      <div className="flex items-center justify-between mb-3">
        <span className="caption-label text-[#A26F3E]">{label}</span>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${highlight ? "bg-white" : "bg-[#FAF3EA]"}`}>
          <Icon className={`w-4 h-4 ${highlight ? "text-[#C68E59]" : "text-[#A26F3E]"}`} />
        </div>
      </div>
      <div className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] leading-none mt-1">{value}</div>
      {sub && <p className="text-xs mt-3 leading-relaxed text-[#666]">{sub}</p>}
    </>
  );
  return to ? <Link to={to} className={cls} data-testid={testid}>{inner}</Link> : <div className={cls} data-testid={testid}>{inner}</div>;
}

function QuickAction({ icon: Icon, label, to, badge, testid }) {
  return (
    <Link
      to={to}
      className="group relative flex flex-col items-start justify-between rounded-3xl bg-white border border-[#EADDC9] p-5 sm:p-6 h-full hover:border-[#C68E59]/50 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] transition-all"
      data-testid={testid}
    >
      <div className="w-11 h-11 rounded-full bg-[#FAF3EA] flex items-center justify-center group-hover:bg-[#C68E59] transition-colors">
        <Icon className="w-5 h-5 text-[#A26F3E] group-hover:text-white transition-colors" />
      </div>
      <p className="font-serif text-base sm:text-lg text-[#1A1A1A] mt-5 leading-tight">{label}</p>
      <ArrowRight className="w-4 h-4 text-[#8B8680] mt-3 group-hover:translate-x-1 transition-transform" />
      {badge > 0 && (
        <span className="absolute top-4 right-4 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#C68E59] text-white text-[10px] font-bold">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}

function ApplicationRow({ app, onUpdate }) {
  const t = app.talent || {};
  const collab = app.collaboration || {};
  const st = STATUS[app.status] || STATUS.sent;
  const photo = t.picture || (app.talent_profile?.demo_photo_url) || null;
  const canActOn = app.status === "sent" || app.status === "viewed" || app.status === "shortlisted";

  return (
    <li className="py-4 flex items-center gap-4" data-testid={`app-row-${app.id}`}>
      <Link to={`/talents/${app.talent_user_id}`} className="shrink-0">
        {photo ? (
          <img src={photo} alt={t.name || "Talent"} className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[#FAF3EA] flex items-center justify-center text-sm font-semibold text-[#A26F3E]">
            {(t.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
      </Link>

      <div className="flex-1 min-w-0">
        <Link to={`/talents/${app.talent_user_id}`} className="font-medium text-[#1A1A1A] hover:underline truncate block">
          {t.name || "Talent"}
        </Link>
        <p className="text-xs text-[#8B8680] truncate">{collab.title || "Collaboration"}</p>
      </div>

      <span className={`hidden sm:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full border ${st.color}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
        {st.label}
      </span>

      {canActOn ? (
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onUpdate(app.id, "accepted")}
            className="w-8 h-8 rounded-full bg-[#E7F5EC] hover:bg-[#2E7D32] hover:text-white text-[#2E7D32] border border-[#BFE4C5] flex items-center justify-center transition"
            title="Accepter"
            data-testid={`accept-${app.id}`}
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={() => onUpdate(app.id, "refused")}
            className="w-8 h-8 rounded-full bg-red-50 hover:bg-[#D32F2F] hover:text-white text-[#D32F2F] border border-red-200 flex items-center justify-center transition"
            title="Refuser"
            data-testid={`refuse-${app.id}`}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <Link
          to={`/pro/collaborations/${app.collaboration_id}/applications`}
          className="text-xs text-[#A26F3E] hover:text-[#1A1A1A] inline-flex items-center gap-1 shrink-0"
        >
          Voir <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </li>
  );
}

function TalentSuggestion({ talent }) {
  const photo = talent.demo_photo_url || talent.main_photo_url || null;
  return (
    <Link
      to={`/talents/${talent.user_id}`}
      className="flex items-center gap-3 p-2 rounded-2xl hover:bg-[#FFFBF5] transition group"
      data-testid={`suggestion-${talent.user_id}`}
    >
      <div className="w-14 h-14 rounded-2xl overflow-hidden bg-[#FAF3EA] shrink-0">
        {photo ? (
          <img src={photo} alt={talent.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sm font-semibold text-[#A26F3E]">
            {(talent.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[#1A1A1A] truncate">{talent.name}</div>
        <div className="flex items-center gap-2 text-xs text-[#8B8680] truncate mt-0.5">
          {talent.talent_type && <span>{labelTalentType(talent.talent_type)}</span>}
          {talent.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {talent.location}
            </span>
          )}
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-[#8B8680] group-hover:text-[#C68E59] group-hover:translate-x-0.5 transition shrink-0" />
    </Link>
  );
}
